import React from "react";
import { Document, Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";
import { format, differenceInMinutes, parse } from "date-fns";
import { RelationshipType, RelationshipTypeLabels } from "../../types/ParentGuardian";
import { getInitials, formatStudentName, formatDuration } from "../../utils";
import logo from "../../assets/hcca-logo.png";

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  preferred_first_name?: string | null;
  profile_picture: string | null;
}

interface ParentGuardian {
  id: string;
  first_name: string;
  last_name: string;
  relationship: RelationshipType;
}

interface CheckInOutRecord {
  created_at: string;
  student: Student;
  parent_guardian: ParentGuardian;
  signature: string;
}

interface PDFDailyAttendanceRecord {
  checkin: CheckInOutRecord | null;
  checkout: CheckInOutRecord | null;
}

interface DailyStats {
  checkedIn: number;
  totalEnrolled: number;
  records: PDFDailyAttendanceRecord[];
}

interface PDFDailyStats {
  [date: string]: DailyStats;
}

interface TeacherDetails {
  id: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
}

// Use standard PDF fonts
Font.register({
  family: 'Helvetica',
  fonts: [
    {
      src: 'Helvetica',
    },
    {
      src: 'Helvetica-Bold',
      fontWeight: 'bold',
    }
  ]
});

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  header: {
    flexDirection: "row",
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    paddingBottom: 20,
    alignItems: "center",
  },
  logo: {
    width: 180,
    height: 60,
    objectFit: "contain",
    marginRight: 20,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
    color: "#1976d2",
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    marginBottom: 5,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 20,
  },
  teacherInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  profilePicture: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
  },
  initialsContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#e0e0e0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  initialsText: {
    fontSize: 10,
    color: '#666',
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
  },
  text: {
    fontSize: 10,
    marginBottom: 4,
    color: "#333",
    fontFamily: 'Helvetica',
  },
  smallText: {
    fontSize: 8,
    color: "#666",
    marginTop: 2,
    fontFamily: 'Helvetica',
  },
  dateHeader: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    marginTop: 15,
    marginBottom: 8,
    borderRadius: 4,
  },
  dateText: {
    fontSize: 14,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
    color: "#333",
    marginBottom: 4,
  },
  table: {
    width: "100%",
    marginTop: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f5f5f5",
    borderBottomWidth: 2,
    borderBottomColor: "#e0e0e0",
    padding: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    minHeight: 50,
    alignItems: "flex-start",
    padding: 8,
  },
  tableCol: {
    flex: 1,
    justifyContent: "flex-start",
  },
  tableCell: {
    fontSize: 10,
    color: "#333",
    fontFamily: 'Helvetica',
  },
  warningText: {
    color: "#b71c1c",
    fontSize: 10,
    fontFamily: 'Helvetica',
    fontWeight: 'bold',
  },
  studentCol: {
    flex: 1.2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeCol: {
    flex: 1.4,
  },
  signature: {
    width: 60,
    height: 30,
    objectFit: 'contain',
    marginTop: 6,
  }
});

interface AttendancePDFProps {
  className: string;
  teacher: TeacherDetails;
  startDate: Date | null;
  endDate: Date | null;
  selectedStudent: string | null;
  attendanceRecords: PDFDailyStats;
  filteredDates: string[];
}

// Helper function to convert URL to base64
const imageSourceFromUrl = (url: string) => ({
  uri: url,
  method: 'GET' as const,
});

export const AttendancePDF: React.FC<AttendancePDFProps> = ({
  className,
  teacher,
  startDate,
  endDate,
  selectedStudent,
  attendanceRecords,
  filteredDates
}) => {
  const hasFilters = startDate || endDate || selectedStudent;

  // Find student name for filter display
  const selectedStudentName = selectedStudent && filteredDates.length > 0 && attendanceRecords[filteredDates[0]]?.records.find(
    r => (r.checkin?.student.id === selectedStudent || r.checkout?.student.id === selectedStudent)
  )?.checkin?.student;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View style={styles.headerInfo}>
            <Text style={styles.title}>Attendance Report</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.subtitle}>Class Information</Text>
          <Text style={styles.text}>Class: {className}</Text>
          <View style={styles.teacherInfo}>
            {teacher.profile_picture ? (
              <Image src={imageSourceFromUrl(teacher.profile_picture)} style={styles.profilePicture} />
            ) : (
              <View style={styles.initialsContainer}>
                <Text style={styles.initialsText}>
                  {getInitials({ first_name: teacher.first_name, last_name: teacher.last_name })}
                </Text>
              </View>
            )}
            <Text style={styles.text}>Teacher: {teacher.first_name} {teacher.last_name}</Text>
          </View>
        </View>

        {hasFilters && (
          <View style={styles.section}>
            <Text style={styles.subtitle}>Applied Filters</Text>
            {(startDate || endDate) && (
              <Text style={styles.text}>
                Date Range: {startDate ? format(startDate, "MM/dd/yyyy") : "All"} - {endDate ? format(endDate, "MM/dd/yyyy") : "All"}
              </Text>
            )}
            {selectedStudentName && (
              <Text style={styles.text}>
                Student: {selectedStudentName.first_name} {selectedStudentName.last_name}
              </Text>
            )}
          </View>
        )}

        {filteredDates.map(date => {
          const dailyStats = attendanceRecords[date];
          if (!dailyStats) return null;

          // Filter records if a student is selected
          const records = selectedStudent
            ? dailyStats.records.filter(r => 
                r.checkin?.student.id === selectedStudent || 
                r.checkout?.student.id === selectedStudent
              )
            : dailyStats.records;

          if (records.length === 0) return null;

          return (
            <View key={date} style={styles.section}>
              <View style={styles.dateHeader}>
                <Text style={styles.dateText}>
                  {format(parse(date, 'yyyy-MM-dd', new Date()), "EEEE, MMMM d, yyyy")}
                </Text>
                <Text style={styles.text}>
                  {selectedStudent 
                    ? `${records.length > 0 ? "Student checked in" : "Student not checked in"}`
                    : `${dailyStats.checkedIn} of ${dailyStats.totalEnrolled} students checked in`
                  }
                </Text>
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <View style={[styles.tableCol, styles.studentCol]}>
                    <Text style={styles.tableCell}>Student</Text>
                  </View>
                  <View style={[styles.tableCol, styles.timeCol]}>
                    <Text style={styles.tableCell}>Check In</Text>
                  </View>
                  <View style={[styles.tableCol, styles.timeCol]}>
                    <Text style={styles.tableCell}>Check Out</Text>
                  </View>
                  <View style={[styles.tableCol, styles.timeCol]}>
                    <Text style={styles.tableCell}>Time in Class</Text>
                  </View>
                </View>

                {records.map((record, index) => {
                  const student = record.checkin?.student || record.checkout?.student;
                  if (!student) return null;

                  return (
                    <View key={`${date}-${index}`} style={styles.tableRow}>
                      <View style={[styles.tableCol, styles.studentCol]}>
                        {student.profile_picture ? (
                          <Image src={imageSourceFromUrl(student.profile_picture)} style={styles.profilePicture} />
                        ) : (
                          <View style={styles.initialsContainer}>
                            <Text style={styles.initialsText}>
                              {getInitials({ first_name: student.first_name, last_name: student.last_name })}
                            </Text>
                          </View>
                        )}
                        <Text style={styles.tableCell}>
                          {formatStudentName(student)}
                        </Text>
                      </View>
                      <View style={[styles.tableCol, styles.timeCol]}>
                        {record.checkin ? (
                          <View>
                            <Text style={styles.tableCell}>
                              {format(new Date(record.checkin.created_at), "h:mm a")}
                            </Text>
                            <Text style={styles.smallText}>
                              {record.checkin.parent_guardian.first_name} {record.checkin.parent_guardian.last_name} ({RelationshipTypeLabels[record.checkin.parent_guardian.relationship as RelationshipType]})
                            </Text>
                            {record.checkin.signature && (
                              <Image src={imageSourceFromUrl(record.checkin.signature)} style={styles.signature} />
                            )}
                          </View>
                        ) : (
                          <Text style={styles.tableCell}>—</Text>
                        )}
                      </View>
                      <View style={[styles.tableCol, styles.timeCol]}>
                        {record.checkout ? (
                          <View>
                            <Text style={styles.tableCell}>
                              {format(new Date(record.checkout.created_at), "h:mm a")}
                            </Text>
                            <Text style={styles.smallText}>
                              {record.checkout.parent_guardian.first_name} {record.checkout.parent_guardian.last_name} ({RelationshipTypeLabels[record.checkout.parent_guardian.relationship as RelationshipType]})
                            </Text>
                            {record.checkout.signature && (
                              <Image src={imageSourceFromUrl(record.checkout.signature)} style={styles.signature} />
                            )}
                          </View>
                        ) : (
                          <Text style={styles.warningText}>Never Checked Out</Text>
                        )}
                      </View>
                      <View style={[styles.tableCol, styles.timeCol]}>
                        <Text style={styles.tableCell}>
                          {(() => {
                            if (record.checkin && record.checkout) {
                              const minutes = differenceInMinutes(
                                new Date(record.checkout.created_at),
                                new Date(record.checkin.created_at)
                              );
                              return formatDuration(minutes);
                            }
                            return "—";
                          })()}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          );
        })}
      </Page>
    </Document>
  );
};

export default AttendancePDF; 